/**
 * Time selection step for custom request flow.
 * Multi-select grid of available start times using EventPage-style cards.
 * @module Widget/CustomRequest/steps/TimeSelectionStep
 */

import React from 'react';
import type { RequestOptionsTime } from '../../lib/types.ts';
import styles from '../CustomRequest.module.css';

interface TimeSelectionStepProps {
  times: RequestOptionsTime[];
  selectedTimes: string[];
  onChange: (times: string[]) => void;
  loading: boolean;
}

/**
 * Converts a display time like "7:00 PM" to HH:mm format "19:00".
 * Falls back to the original string if parsing fails.
 */
function toHHmm(localTime: string): string {
  const match = localTime.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return localTime;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = match[3].toUpperCase();
  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  return `${hours.toString().padStart(2, '0')}:${minutes}`;
}

/**
 * Multi-select list of available start times fetched from request-options API.
 *
 * @param props - Component props
 * @returns Rendered time selection step
 */
const TimeSelectionStep: React.FC<TimeSelectionStepProps> = ({
  times,
  selectedTimes,
  onChange,
  loading,
}) => {
  const toggleTime = (timeValue: string) => {
    if (selectedTimes.includes(timeValue)) {
      onChange(selectedTimes.filter((t) => t !== timeValue));
    } else {
      onChange([...selectedTimes, timeValue]);
    }
  };

  const selectAll = () => {
    const allValues = times.map((t) => toHHmm(t.local));
    onChange(allValues);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <div className={styles.spinner} />
        <p className={styles.sectionSubtitle}>Loading available times...</p>
      </div>
    );
  }

  if (times.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className={styles.centeredText}>
          No time slots available for this date and party size.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className={styles.sectionSubtitle}>
          Select your ideal start times. We suggest at least 4 to increase your chance of confirmation.
        </p>
        <button className={styles.selectAllLink} onClick={selectAll}>
          Select all
        </button>
      </div>

      <div className={styles.timeGrid}>
        {times.map((time) => {
          const value = toHHmm(time.local);
          const isSelected = selectedTimes.includes(value);
          return (
            <button
              key={value}
              className={isSelected ? styles.timeCardSelected : styles.timeCard}
              onClick={() => toggleTime(value)}
            >
              {time.local}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TimeSelectionStep;
